import { format } from 'date-fns';
import { Building2, DollarSign, Target, Users } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { useOverview } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';
import { subscriptionStatusBadgeVariant, subscriptionStatusLabel } from '../lib/subscriptionStatus';

const PLAN_COLORS: Record<string, string> = {
  free: 'hsl(0, 0%, 55%)',
  starter: 'hsl(217, 91%, 60%)',
  growth: 'hsl(142, 71%, 45%)',
  enterprise: 'hsl(280, 67%, 60%)',
};

function planBadgeVariant(plan: string): 'default' | 'info' | 'success' | 'primary' {
  switch (plan.toLowerCase()) {
    case 'starter':
      return 'info';
    case 'growth':
      return 'success';
    case 'enterprise':
      return 'primary';
    default:
      return 'default';
  }
}

export function Overview() {
  const { data, isLoading } = useOverview();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Overview</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Organizations"
          value={data?.totalOrganizations ?? 0}
          icon={Building2}
          loading={isLoading}
        />
        <StatCard
          label="Total Users"
          value={data?.totalUsers ?? 0}
          icon={Users}
          loading={isLoading}
        />
        <StatCard
          label="Total Leads"
          value={data?.totalLeads ?? 0}
          icon={Target}
          loading={isLoading}
        />
        <StatCard
          label="Monthly AI Cost"
          value={data ? `$${parseFloat(data.monthlyAiCost).toFixed(2)}` : '$0.00'}
          icon={DollarSign}
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">
            Subscription Distribution
          </h2>
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-text-muted">Loading...</div>
            </div>
          ) : data?.planDistribution && data.planDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={264}>
              <PieChart>
                <Pie
                  data={data.planDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="count"
                  nameKey="plan"
                  paddingAngle={2}
                  label={({ name, value }) => `${name ?? ''} (${value})`}
                >
                  {data.planDistribution.map((entry) => (
                    <Cell
                      key={entry.plan}
                      fill={PLAN_COLORS[entry.plan.toLowerCase()] || PLAN_COLORS.free}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(225, 20%, 16%)',
                    border: '1px solid hsl(225, 15%, 25%)',
                    borderRadius: '8px',
                    color: 'hsl(0, 0%, 95%)',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Organizations by Plan</h2>
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-text-muted">Loading...</div>
            </div>
          ) : data?.planDistribution && data.planDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={264}>
              <BarChart data={data.planDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(225, 15%, 18%)" />
                <XAxis dataKey="plan" stroke="hsl(0, 0%, 45%)" fontSize={12} />
                <YAxis stroke="hsl(0, 0%, 45%)" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(225, 20%, 16%)',
                    border: '1px solid hsl(225, 15%, 25%)',
                    borderRadius: '8px',
                    color: 'hsl(0, 0%, 95%)',
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.planDistribution.map((entry) => (
                    <Cell
                      key={entry.plan}
                      fill={PLAN_COLORS[entry.plan.toLowerCase()] || PLAN_COLORS.free}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-text-secondary">Recent Organizations</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Plan</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Members</th>
                <th className="px-5 py-3 font-medium">Leads</th>
                <th className="px-5 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border animate-pulse">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-5 py-3">
                        <div className="h-4 bg-border rounded w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data?.recentOrganizations?.length ? (
                data.recentOrganizations.map((org) => (
                  <tr
                    key={org.id}
                    className="border-b border-border hover:bg-surface-hover transition-colors cursor-pointer"
                    onClick={() => navigate(`/organizations/${org.id}`)}
                  >
                    <td className="px-5 py-3 text-text font-medium">{org.name}</td>
                    <td className="px-5 py-3">
                      <Badge variant={planBadgeVariant(org.plan)}>{org.plan}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={subscriptionStatusBadgeVariant(org.status)}>
                        {subscriptionStatusLabel(org.status, org.planExpiredAt)}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-text-secondary">{org.memberCount}</td>
                    <td className="px-5 py-3 text-text-secondary">{org.leadCount}</td>
                    <td className="px-5 py-3 text-text-secondary">
                      {format(new Date(org.createdAt), 'MMM d, yyyy')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-text-muted">
                    No organizations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
